import { useQueries } from '@tanstack/react-query'
import type { SourceName } from '@xweather/protect-types'
import { useMemo } from 'react'
import { dayjs } from '../../dayjs.ts'
import type { LayerViewId } from '../../features/layers/layers.tsx'
import { useActiveLayerIds, useIsLayerActive } from '../../features/layers/layerUtils.ts'
import { useTimeline } from '../../features/timeline/timelineState.ts'
import { type FeatureFlag, useFlags } from '../../launchdarkly.ts'
import { useImpersonation } from '../impersonation.ts'
import { LTZ_SUFFIX, useLightningProtections } from '../protections/lightning.ts'
import { type AnyProtection, type LightningProtection, ProtectionType } from '../protections/types.ts'
import { useProtections } from '../protections/useProtections.ts'
import { useSubscription } from '../service.ts'
import { alertsQuery, useHistoricalAlerts } from './alerts.ts'
import { realtimeLightningAlertQuery, useHistoricalLightningAlerts, useLightningAlerts, useLtzAlerts } from './lightning.ts'
import type { AnyAlert, Severity } from './types.ts'
import { severityAsNumberMap } from './utils.ts'

type AssetAlertState<TProtection> = {
	protection: TProtection | undefined
	severity: Severity | undefined
}

export const useAlerts = () => {
	const impersonation = useImpersonation()
	const { protections, isLoading, isPending } = useProtections()
	const { data: subscription } = useSubscription()
	const hasProtectionsSub = subscription?.features.includes('protections')
	const ltzAlerts = useLtzAlerts()

	return useQueries({
		queries: [
			alertsQuery(impersonation, { enabled: hasProtectionsSub }),
			realtimeLightningAlertQuery(impersonation, { enabled: hasProtectionsSub }),
		],
		combine: (queries) => {
			const [general, realtime] = queries
			const all: AnyAlert[] = [
				...(general.data ?? []).filter((a) => a.type !== ProtectionType.Lightning),
				...(realtime.data ?? []),
				...ltzAlerts.data,
			]
			const alertsWithProts = all.filter((a) => protections[a.protectionVrn])
			const alerts = Object.entries(Object.groupBy(alertsWithProts, (p) => p.protectionVrn)).reduce(
				(acc, [vrn, protList]) => {
					// We do not want to add ltz alerts if there is a realtime alarm
					if (protList?.[0] && !acc[vrn.replace(LTZ_SUFFIX, '')]) acc[vrn] = protList?.[0]
					return acc
				},
				{} as Record<string, (typeof all)[0]>,
			)
			return {
				isPending: isPending || ltzAlerts.isPending || queries.some((q) => q.isPending),
				isLoading: isLoading || ltzAlerts.isLoading || queries.some((q) => q.isLoading),
				totalAlerts: Object.keys(alerts).length,
				alerts,
			}
		},
	})
}

export const useCurrentlyActiveProts = () => {
	const { protections } = useProtections()
	const { alerts } = useAlerts()

	return useMemo(() => {
		return Object.values(alerts).flatMap((alert) => {
			const protection = protections[alert.protectionVrn]
			return protection ? [protection] : []
		})
	}, [alerts, protections])
}

export const useActiveOnTimelineLightningAlertState = () => {
	const date = useTimeline((s) => s.date)
	const startTime = useTimeline((s) => s.startTime)
	const endTime = useTimeline((s) => s.endTime)
	const isNowLocked = useTimeline((s) => s.nowLocked)
	const isLightningLayerActive = useIsLayerActive('lightning-alert-rings', 'lightning-realtime')
	const { data: protections = [] } = useLightningProtections()
	const { data: alerts = [] } = useLightningAlerts()
	const { data: oldAlerts = [] } = useHistoricalLightningAlerts(startTime, endTime, !isNowLocked && isLightningLayerActive, true)

	const resolvedAlerts = useMemo(() => {
		// useHistoricalLightningAlerts use previous data as placeholder for clean replay but if we disable
		// the layer we need to make sure to not return those old alerts + we also want to make sure to not return any current alerts
		if (!isLightningLayerActive) return []
		const protectionsMap = new Map(protections.map((p) => [p.vrn, p]))
		return [...alerts, ...oldAlerts].flatMap((a) => {
			const protection = protectionsMap.get(a.protectionVrn)
			if (!protection) return []
			return [{ protection, start: dayjs(a.firstTrigger).valueOf(), end: dayjs(a.eventTime).valueOf(), severity: a.severity }]
		})
	}, [alerts, oldAlerts, protections, isLightningLayerActive])

	const ts = date.valueOf()
	return useMemo(() => resolvedAlerts.filter((a) => ts >= a.start && ts <= a.end), [resolvedAlerts, ts])
}

const TIMELINE_ALERT_SOURCES: {
	source: SourceName
	layerIds: LayerViewId[]
	flag?: FeatureFlag
}[] = [
	{ source: 'HAIL_THREAT', layerIds: ['hail-threats'] },
	{ source: 'WEATHER_HAZARD', layerIds: ['alerts'] },
	{ source: 'ERG_ECMWF_HRES', layerIds: ['wind-particles', 'wind-gusts'] },
	{ source: 'XWEATHER_FORECASTS', layerIds: ['radar'], flag: 'precipAlertReplay' },
]

const useActiveOnTimelineAlerts = () => {
	const startTime = useTimeline((s) => s.startTime)
	const endTime = useTimeline((s) => s.endTime)
	const isNowLocked = useTimeline((s) => s.nowLocked)
	const activeLayerIds = useActiveLayerIds()
	const flags = useFlags()

	const sources = isNowLocked
		? []
		: TIMELINE_ALERT_SOURCES.filter(
				({ layerIds, flag }) => layerIds.some((id) => activeLayerIds.includes(id)) && (!flag || flags[flag]),
			).map((s) => s.source)

	return useHistoricalAlerts(startTime, endTime, sources)
}

export const useActiveOnTimelineAlertState = () => {
	const date = useTimeline((s) => s.date)
	const alerts = useActiveOnTimelineAlerts()

	const { protections } = useProtections()

	const resolvedAlerts = useMemo(() => {
		return alerts.flatMap((alert) => {
			const protection = protections[alert.protectionVrn]
			if (!protection) return []
			return [
				{
					protection,
					start: dayjs(alert.firstEventTime).valueOf(),
					end: alert.clearTime ? dayjs(alert.clearTime).valueOf() : dayjs().valueOf(),
					severity: alert.severity,
				},
			]
		})
	}, [alerts, protections])

	const ts = date.valueOf()
	return resolvedAlerts.filter((a) => ts >= a.start && ts <= a.end)
}

export const useActiveOnTimelineMostSevereAlertState = () => {
	const active = useActiveOnTimelineAlertState()
	const { protections } = useProtections()

	return useMemo(() => {
		const byAsset = Object.values(protections).reduce(
			(acc, protection) => {
				acc[protection.assetVrn] = { protection: undefined, severity: undefined }
				return acc
			},
			{} as Record<string, AssetAlertState<AnyProtection>>,
		)

		for (const { protection, severity } of active) {
			if (!severity) continue

			const assetVrn = protection.assetVrn
			const current = byAsset[assetVrn]

			if (!current?.severity || severityAsNumberMap[severity] > severityAsNumberMap[current.severity]) {
				byAsset[assetVrn] = { protection, severity }
			}
		}

		return byAsset
	}, [active, protections])
}

export const useActiveOnTimelineCombinedMostSevereAlertState = () => {
	const lightningState = useActiveOnTimelineLightningMostSevereAlertState()
	const generalState = useActiveOnTimelineMostSevereAlertState()

	return useMemo(() => {
		const merged: Record<string, AssetAlertState<AnyProtection>> = { ...generalState }
		for (const [assetVrn, lightning] of Object.entries(lightningState)) {
			const existing = merged[assetVrn]
			const lightSev = lightning?.severity
			const existSev = existing?.severity
			if (!existSev || (lightSev && severityAsNumberMap[lightSev] > severityAsNumberMap[existSev])) {
				merged[assetVrn] = lightning
			}
		}
		return merged
	}, [lightningState, generalState])
}

export const useCurrentMostSevereAlertState = () => {
	const { protections } = useProtections()
	const { alerts } = useAlerts()

	return useMemo(() => {
		const byAsset = Object.values(protections).reduce(
			(acc, protection) => {
				acc[protection.assetVrn] = { protection: undefined, severity: undefined }
				return acc
			},
			{} as Record<string, AssetAlertState<AnyProtection>>,
		)

		for (const alert of Object.values(alerts) as AnyAlert[]) {
			const protection = protections[alert.protectionVrn]
			if (!protection || !alert.severity) continue

			const assetVrn = protection.assetVrn
			const current = byAsset[assetVrn]

			if (!current?.severity || severityAsNumberMap[alert.severity] > severityAsNumberMap[current.severity]) {
				byAsset[assetVrn] = { protection, severity: alert.severity }
			}
		}

		return byAsset
	}, [alerts, protections])
}

export const useActiveOnTimelineLightningMostSevereAlertState = () => {
	const active = useActiveOnTimelineLightningAlertState()
	const { data: protections = [] } = useLightningProtections()

	return useMemo(() => {
		const byAsset = protections.reduce(
			(acc, protection) => {
				acc[protection.assetVrn] = { protection: undefined, severity: undefined }
				return acc
			},
			{} as Record<string, AssetAlertState<LightningProtection>>,
		)

		for (const { protection, severity } of active) {
			if (!severity) continue

			const assetVrn = protection.assetVrn
			const highestAlertState = byAsset[assetVrn]

			if (!highestAlertState?.severity || severityAsNumberMap[severity] > severityAsNumberMap[highestAlertState.severity]) {
				byAsset[assetVrn] = { protection, severity }
			}
		}

		return byAsset
	}, [active, protections])
}

export const useAssetAlertState = (assetVrn: string) => {
	const isNowLocked = useTimeline((s) => s.nowLocked)
	const currentState = useCurrentMostSevereAlertState()
	const timelineState = useActiveOnTimelineCombinedMostSevereAlertState()
	return useMemo(() => {
		if (isNowLocked) return currentState[assetVrn]
		const state = timelineState[assetVrn]
		return state
	}, [isNowLocked, currentState, timelineState, assetVrn])
}
